import React from "react";
import "./ProgressBar.scss";
import {Progress} from "antd";
import {TranslateFunction} from "react-localize-redux";
import {useSelector} from "react-redux";
import {formatString} from "../../utils/common";
import {TaskPhaseStats} from "../../interfaces/store/actions/payload/task";
import {ProgressBarColors} from "../../enums/enums";
import {selectTranslate} from "../../store/selectors/translate";

interface Props {
    phaseStats: Object
    taskCount: number
    completedTaskCount: number
}

interface ProgressBarData {
    progressValue: number
    markAsRejected: boolean
}

const ProgressBar: React.FC<Props> = ({phaseStats, taskCount, completedTaskCount}: Props): React.ReactElement => {
    const translatePath: string = "components.ProgressBar.ProgressBar";
    const translate: TranslateFunction = useSelector(selectTranslate);

    const getPercentageFormat = (percent: number | undefined): React.ReactNode => (
        <span>
            {formatString(translate(`${translatePath}.percentageFormat.text`) as string, (percent as number).toString())}
        </span>
    );

    /**
     * Collect progress value and rejected status for progress bars
     * @return {ProgressBarData}
     */
    const getProgressBarData = (): ProgressBarData => {
        const {REJECTED = 0, SKIPPED = 0}: TaskPhaseStats = phaseStats;

        const totalTasks: number = taskCount - SKIPPED;
        const progressValue: number = totalTasks ? Math.round((completedTaskCount / totalTasks) * 100) : totalTasks;
        const markAsRejected: boolean = !!(totalTasks && REJECTED === totalTasks);

        return {
            progressValue,
            markAsRejected,
        };
    };

    /**
     * Based on completion percentage, we color progress bar in appropriate color
     * @param {number} tasksCompletionPercentage
     * @param {boolean} markAsRejected
     * @return {string} strokeColor
     */
    const getProgressColor = (tasksCompletionPercentage: number, markAsRejected: boolean): string => {
        let strokeColor: string = ProgressBarColors.IN_PROGRESS;
        if (tasksCompletionPercentage === 100) {
            if (markAsRejected) {
                strokeColor = ProgressBarColors.REJECTED;
            } else {
                strokeColor = ProgressBarColors.COMPLETED;
            }
        }

        return strokeColor;
    };

    const {progressValue, markAsRejected}: ProgressBarData = getProgressBarData();

    return (
        <div className="df-progress-container">
            <Progress
                strokeWidth={6}
                percent={progressValue}
                format={getPercentageFormat}
                strokeColor={getProgressColor(progressValue, markAsRejected)}
            />
        </div>
    );
};

export default ProgressBar;
